'use client';
import {
  Menu,
  MenuItem,
  Link,
  Box,
  Fade,
  useTheme,
  Typography,
  Divider,
  Stack,
  Button,
  Collapse,
} from '@mui/material';
import React, { useRef, useState } from 'react';
import { Squash as Hamburger } from 'hamburger-react';
import { HeaderProps } from '../types';
import Optional from '../../../Optional';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Logo from '../../../logo';
import { trackEvent } from '../../../../utils/trackEvent';
import { FIXED_HEIGHT } from './constant';
import { usePathname } from '../../../../utils/routing';
import useScroll from '../fixed/useScroll';
import { useNavTheme } from '../NavThemeContext';

type Props = Pick<HeaderProps, 'navigation' | 'logo' | 'variant'>;

const MobileMenu = ({ navigation, logo, variant }: Props) => {
  const { customConfig, cta, links } = navigation;
  const pathname = usePathname();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedLinks, setExpandedLinks] = useState<Set<string>>(new Set());

  const ref = useRef(null);
  const handleOpen = () => {
    setAnchorEl(ref.current);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setExpandedLinks(new Set());
  };
  const toggleLink = (label: string) => {
    setExpandedLinks((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };
  const onToggle = (toggled: boolean) => {
    if (toggled) return handleOpen();
    return handleClose();
  };
  const isFixed = variant === 'fixed';
  const { isTop } = useScroll();
  const { topBarTheme } = useNavTheme();
  const closedHamburgerColor =
    isFixed && isTop && topBarTheme === 'dark'
      ? theme.palette.common.white
      : theme.palette.common.black;
  return (
    <>
      <Optional condition={!!cta}>
        <Fade in={!anchorEl} appear={false}>
          <Button
            variant={'contained'}
            component={Link}
            href={cta?.href}
            onClick={() => {
              trackEvent('select_content', {
                content_type: 'cta',
                placement: 'nav',
                item_id: cta?.label,
              });
            }}
          >
            {cta?.label}
          </Button>
        </Fade>
      </Optional>
      <Box ref={ref}>
        <Optional condition={!!links.length}>
          <Hamburger
            label={'Show Menu'}
            direction={'left'}
            size={24}
            onToggle={onToggle}
            toggled={!!anchorEl}
            rounded
            color={closedHamburgerColor}
          />
        </Optional>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        variant={'menu'}
        slots={{
          transition: Fade,
        }}
        slotProps={{
          list: {
            sx: { pt: isFixed ? 0.5 : 0 },
          },
          paper: {
            elevation: 0,
            sx: {
              background: theme.palette.background.default,
              borderRadius: 0.5,
              borderTopRightRadius: 0,
              borderTopLeftRadius: 0,
              width: '100vw',
              maxWidth: '100vw',
              py: 1,
              px: 3,
            },
          },
          backdrop: {
            sx: {
              background: theme.alpha(theme.palette.common.black, 0.3),
              backdropFilter: 'blur(10px)',
            },
          },
        }}
        anchorPosition={{
          top: 0,
          left: 0,
        }}
        marginThreshold={0}
        anchorReference={'anchorPosition'}
      >
        <Stack
          direction={'row'}
          sx={{ mb: 8, justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Logo
            light={logo.main}
            dark={logo.onDark}
            {...customConfig?.logo}
            withLink
            mode={'light'}
            onClick={handleClose}
            height={isFixed ? FIXED_HEIGHT.m : customConfig?.logo?.height}
          />
          <Hamburger
            label={'Hide menu'}
            direction={'left'}
            size={24}
            rounded
            onToggle={onToggle}
            toggled={!!anchorEl}
            color={theme.palette.text.primary}
          />
        </Stack>
        {links?.map((link, index) => (
          <Box key={link.label}>
            <Optional condition={!!index}>
              <Divider variant={'middle'} />
            </Optional>
            {link.children?.length ? (
              <>
                <MenuItem
                  onClick={() => toggleLink(link.label)}
                  sx={{
                    flex: 1,
                    color:
                      link.href && link.href === pathname
                        ? theme.palette.primary.main
                        : 'inherit',
                    minHeight: '38px',
                    py: 2,
                  }}
                >
                  <Stack
                    sx={{
                      width: '100%',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                    direction={'row'}
                  >
                    <Typography variant="h5" component="span">
                      {link.label}
                    </Typography>
                    <KeyboardArrowDownIcon
                      sx={{
                        transition: 'transform 0.2s',
                        transform: expandedLinks.has(link.label)
                          ? 'rotate(180deg)'
                          : 'none',
                      }}
                    />
                  </Stack>
                </MenuItem>
                <Collapse in={expandedLinks.has(link.label)}>
                  {link.href && (
                    <MenuItem
                      component={Link}
                      onClick={handleClose}
                      href={link.href}
                      sx={{
                        color:
                          link.href === pathname
                            ? theme.palette.primary.main
                            : 'inherit',
                        minHeight: '38px',
                        py: 1.5,
                        pl: 4,
                      }}
                    >
                      {link.label}
                    </MenuItem>
                  )}
                  {link.children.map((child, idx) => {
                    const showGroup =
                      child.group &&
                      (idx === 0 ||
                        child.group !== link.children![idx - 1]?.group);
                    return (
                      <Box key={child.label}>
                        {showGroup ? (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              pl: 4,
                              pt: idx === 0 ? 0 : 1,
                              color: theme.palette.text.secondary,
                              textTransform: 'uppercase',
                              letterSpacing: '0.08em',
                            }}
                          >
                            {child.group}
                          </Typography>
                        ) : (
                          <Divider variant="middle" sx={{ ml: 4 }} />
                        )}
                        <MenuItem
                          component={Link}
                          href={child.href}
                          onClick={handleClose}
                          sx={{
                            color:
                              child.href === pathname
                                ? theme.palette.primary.main
                                : 'inherit',
                            py: 1.5,
                            pl: 4,
                          }}
                        >
                          <Typography variant="body1" component="span">
                            {child.label}
                          </Typography>
                        </MenuItem>
                      </Box>
                    );
                  })}
                </Collapse>
              </>
            ) : (
              <MenuItem
                component={Link}
                href={link.href}
                onClick={handleClose}
                sx={{
                  color:
                    link.href === pathname
                      ? theme.palette.primary.main
                      : 'inherit',
                  minHeight: '38px',
                  py: 2,
                }}
              >
                <Stack
                  sx={{
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                  direction={'row'}
                >
                  <Typography variant="h5" component="span">
                    {link.label}
                  </Typography>
                  <ArrowRightIcon />
                </Stack>
              </MenuItem>
            )}
          </Box>
        ))}
        <Optional condition={!!cta}>
          <MenuItem component={'div'} sx={{ mt: 6 }} onClick={handleClose}>
            <Button variant={'contained'} component={Link} href={cta?.href}>
              {cta?.label}
            </Button>
          </MenuItem>
        </Optional>
      </Menu>
    </>
  );
};

export default MobileMenu;
